import React, { useEffect, useCallback } from 'react'
import { Masonry } from 'masonic'
import { useWindowSize } from '@react-hook/window-size'
import { inject } from 'mobx-react'
import { PageContent } from '../../components/Common/PageContent'
import { PlusIcon } from '@heroicons/react/24/outline'
import usePagination from '../../utils/usePagination'
import { API, Timeline } from '../../services/api'
import { History } from 'history'
import { SectionHeader } from '../../components/SectionHeader/SectionHeader'
import { TwButton } from '../../components/Button/TwButton'
import { RouteComponentProps } from 'react-router'
import { WORKSPACES_STORE } from '../../stores'
import { WorkspacesStore } from '../../stores/WorkspaceStore'
import { TimelineCard } from './components/TimelineCard'
import { FetchingLoader } from '../../components/FetchingLoader'
import { mobileBreakpoint } from '../../theme'

const TIMELINES_PER_PAGE = 20
const GUTTER = 16

interface TimelinesScreenParams {
  workspaceSlug: string
}

interface TimelinesScreenProps extends RouteComponentProps<TimelinesScreenParams> {
  [WORKSPACES_STORE]: WorkspacesStore
  history: History
}

interface MasonryCardProps {
  index: number
  data: Timeline
  width: number
}

const TimelinesScreen: React.FC<TimelinesScreenProps> = (props) => {
  const workspacesStore = props[WORKSPACES_STORE]
  const workspaceId = workspacesStore.activeWorkspaceId
  const workspaceSlug = workspacesStore.activeWorkspaceSlug
  const [windowWidth] = useWindowSize()

  const fetchTimelinesPage = async (page: number, perPage: number) => {
    const response = await API.getInstance().getTimelines({
      workspaceId,
      count: perPage,
      from: page * perPage,
    })
    if (response.ok && response.data) {
      return response.data
    } else {
      throw new Error(JSON.stringify(response))
    }
  }

  const { data, isFetching, allFetched, fetchNext, reset } = usePagination<Timeline>({
    fetchPage: fetchTimelinesPage,
    perPage: TIMELINES_PER_PAGE,
  })

  // Fetch first page and reset on workspace change
  useEffect(() => {
    reset()
    // Use setTimeout so reset's state update is flushed before fetchNext reads it
    setTimeout(() => fetchNext().catch(() => {}))
  }, [workspaceSlug])

  // Scroll-based fetch: check if user scrolled near the bottom
  useEffect(() => {
    if (allFetched || isFetching || data.length === 0) return

    const handleScroll = () => {
      const scrollBottom = window.innerHeight + window.scrollY
      const threshold = document.documentElement.scrollHeight - 300
      if (scrollBottom >= threshold && !allFetched && !isFetching) {
        fetchNext().catch(() => {})
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    // Also check immediately in case content doesn't fill the viewport
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [data.length, allFetched, isFetching, fetchNext])

  const calculateColumnCount = (width: number): number => {
    if (width < mobileBreakpoint.width) return 2
    if (width < 1024) return 3
    return 4
  }

  const columnCount = calculateColumnCount(windowWidth)
  const columnWidth = Math.floor((windowWidth - GUTTER * (columnCount - 1)) / columnCount)

  const handleTimelineClick = (timelineId: string) => {
    props.history.push(`/${workspaceSlug}/timelines/${timelineId}`)
  }

  const MasonryCard = useCallback(
    ({ data: timeline, width: itemWidth, index }: MasonryCardProps) => (
      <TimelineCard
        timeline={timeline}
        width={itemWidth}
        tabIndex={index + 1}
        onClick={() => handleTimelineClick(timeline.id)}
      />
    ),
    [handleTimelineClick]
  )

  return (
    <PageContent>
      <SectionHeader
        title="Twoje osi czasu"
        description="Przeglądaj i twórz osi czasu, aby organizować wspomnienia w chronologicznej kolejności. Dziel się historiami ze znajomymi! 📖"
        actions={
          <TwButton
            variant="outline"
            icon={<PlusIcon className="!h-4 !w-4" />}
            onClick={() => {
              // TODO: Add timeline creation
              console.log('Create timeline')
            }}
          >
            Stwórz nową oś czasu
          </TwButton>
        }
      />
      <div className="pb-5">
        {data.length > 0 && (
          <div style={{ padding: '20px 0', width: '100%' }}>
            <Masonry
              items={data}
              columnCount={columnCount}
              columnWidth={columnWidth}
              columnGutter={GUTTER}
              rowGutter={GUTTER}
              overscanBy={5}
              render={MasonryCard}
              itemKey={(item) => item.id}
            />
          </div>
        )}
        {!isFetching && data.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <p className="text-lg">Brak osi czasu</p>
            <p className="text-sm">Stwórz swoją pierwszą oś czasu, aby rozpocząć!</p>
          </div>
        )}
        {isFetching && <FetchingLoader />}
      </div>
    </PageContent>
  )
}

export default inject(WORKSPACES_STORE)(TimelinesScreen)
